import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Sparkles,
  ArrowLeft,
  FileText,
  Loader2,
  Lock,
  Calendar,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function SharedView() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/share/${token}`)
      .then((r) => setDoc(r.data))
      .catch(() => setError("This shared document was not found or has expired."));
  }, [token]);

  return (
    <div
      className="min-h-screen bg-[#020617] text-slate-50 terminal-grain"
      data-testid="shared-view"
    >
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-[#020617]/95 backdrop-blur">
        <div className="mx-auto max-w-3xl px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-7 w-7 grid place-items-center bg-cyan-500 text-slate-950 rounded-sm">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-base tracking-tight">
              3C<span className="text-cyan-400"> AI</span>
            </span>
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 ml-2">
              Engineering Intelligence
            </span>
          </Link>
          <Link to="/">
            <Button
              size="sm"
              variant="outline"
              className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
              data-testid="open-platform-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
              Open platform
            </Button>
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-6 py-10">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-2 flex items-center gap-2">
          <span className="inline-block h-1 w-6 bg-cyan-500" />
          Shared Document · Read-only
        </div>

        {error ? (
          <div className="bg-rose-500/10 border border-rose-700 rounded-sm p-6 text-rose-200 text-sm">
            {error}
          </div>
        ) : !doc ? (
          <div className="text-center py-20">
            <Loader2 className="h-6 w-6 text-cyan-400 animate-spin mx-auto mb-3" />
            <div className="text-xs font-mono text-cyan-400 uppercase tracking-[0.2em]">
              Loading shared document…
            </div>
          </div>
        ) : (
          <article
            className="bg-slate-900/60 border border-slate-800 rounded-sm"
            data-testid="shared-doc"
          >
            <div className="p-5 border-b border-slate-800 flex flex-wrap items-center gap-3">
              <FileText className="h-4 w-4 text-cyan-400" />
              <h1 className="font-display text-lg font-semibold flex-1">
                {doc.title}
              </h1>
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                {doc.kind}
              </span>
            </div>
            <div className="p-5">
              {doc.body ? (
                <pre
                  data-testid="shared-body"
                  className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-slate-200"
                >
                  {doc.body}
                </pre>
              ) : doc.data ? (
                <pre
                  data-testid="shared-data"
                  className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-slate-200"
                >
                  {JSON.stringify(doc.data, null, 2)}
                </pre>
              ) : (
                <div className="text-sm text-slate-500">
                  This share has no content.
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-800 flex flex-wrap items-center gap-4 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Lock className="h-3 w-3" /> Read-only
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                {(doc.created_at || "").slice(0, 10)}
              </span>
              {doc.project_name && (
                <span className="ml-auto normal-case tracking-normal text-[11px] text-slate-400">
                  {doc.project_name}
                </span>
              )}
            </div>
          </article>
        )}

        <p className="text-center text-[10px] font-mono uppercase tracking-[0.25em] text-slate-600 mt-8">
          Generated using 3C AI — Contract Intelligence Platform
        </p>
      </main>
    </div>
  );
}
